import React, { useCallback, useMemo, useState } from "react";
import { useOutletContext } from "react-router-dom";
import { useApiData, apiFetch } from "../../lib/api.js";
import { getCurrentUser } from "../../lib/auth.js";
import { CalendarDays } from "lucide-react";
import RozvrhVzorGrid from "../../components/RozvrhVzorGrid.jsx";

// Trener portál — osobní pohled na "obecný" rozvrh trenéra.
// Trenér jen vidí (read-only). Editovat může jen sales / owner / manager / head_coach.
// Pevný klient override jen owner / manager / head_coach.
export default function RozvrhVzor({ trenerProp, bezHlavicky = false, jenCteni = false } = {}) {
  // trenerProp = manažerský pohled (vybraný trenér); jinak z outlet contextu (trenér/owner).
  const ctx = useOutletContext() || {};
  const activeTrener = trenerProp || ctx.activeTrener;
  const user = getCurrentUser();
  const isManagement = user && ["owner", "manager", "head_coach"].includes(user.role);
  const isSales = user?.role === "sales";
  const isOwnTrener = user?.role === "trener" && user?.linkedTrener === activeTrener;
  // editable (slot override volno/oběd/mimo) — sales i management. Trener NE.
  // jenCteni (20. 10. 2026): náhled pro sales v Rozvrzích trenérů, nic se nedá upravit.
  const editable = !jenCteni && (isManagement || isSales);
  const canFixClient = !jenCteni && isManagement;
  // canEditAbsence — kdo může přidat/smazat absence:
  //   - management: bez omezení (i zpětně, i krátké)
  //   - trener: jen vlastní, jen ≥14 dnů dopředu
  const canEditAbsence = !jenCteni && (isManagement || isOwnTrener);
  const canBackdateAbsence = isManagement; // jen management může zpětně
  const minAbsenceDaysAhead = isManagement ? 0 : 14; // trenér min 14 dní dopředu
  const [busy, setBusy] = useState(false);
  const [toast, setToast] = useState(null); // { type, msg }
  // Undo history — posledních 3 akcí. Každá je { label, snapshots: [{dow, hour, type, klienti, overridden}] }
  const [history, setHistory] = useState([]);
  const pushHistory = useCallback((label, snapshots) => {
    setHistory((h) => [{ label, snapshots, ts: Date.now() }, ...h].slice(0, 3));
  }, []);

  const showToast = useCallback((type, msg) => {
    setToast({ type, msg });
    setTimeout(() => setToast(null), 2500);
  }, []);

  const url = activeTrener ? `/api/trener-rozvrh-pattern?pilot=${encodeURIComponent(activeTrener)}` : null;
  // bezKese: rozvrh se musí načíst vždy čerstvý. S keší se po obnovení stránky
  // na okamžik ukázal starý stav slotu (např. Volno místo Nepracuje), než
  // dorazila aktuální data, a vypadalo to, že se nastavení neuložilo.
  const { data, setData, refetch, refreshing } = useApiData(url, { bezKese: true });

  // Optimistic update — okamžitě uprav grid lokálně, backend dohnaný v pozadí.
  // Šetří 1-2s refetch (4 sheety) za každou akci. Po POST pokud OK, ne refetchovat.
  const optimisticGridUpdate = useCallback((updates) => {
    // updates: [{ dow, hour, type, klient? }] nebo [{ dow, hour, reset: true }]
    if (!data?.grid) return;
    const updateMap = new Map(updates.map((u) => [`${u.dow}-${u.hour}`, u]));
    const newGrid = data.grid.map((cell) => {
      const u = updateMap.get(`${cell.dow}-${cell.hour}`);
      if (!u) return cell;
      if (u.reset) {
        // Reset → musíme refetch ať backend pošle původní (auto) typ. Zatím zůstavíme volno.
        return { ...cell, type: "volno", klienti: [], overridden: false };
      }
      // Override change
      if (u.type === "fixed" && u.klient) {
        return { ...cell, type: "fixed", klienti: [u.klient], ciziKlienti: [false], pilotNames: [""], overridden: true };
      }
      if (u.type === "preliminary" && u.klient) {
        return { ...cell, type: "preliminary", klienti: [u.klient], preliminaryTrialDate: u.trialDate || "", overridden: true };
      }
      return { ...cell, type: u.type, klienti: u.type === "fixed" ? cell.klienti : [], ciziKlienti: u.type === "fixed" ? cell.ciziKlienti : [], pilotNames: u.type === "fixed" ? cell.pilotNames : [], overridden: true };
    });
    setData({ ...data, grid: newGrid });
  }, [data, setData]);

  // Pevný klient = SEED z celé databáze aktivních klientů (ne jen klienti tohoto trenéra).
  // Owner/manager může přidat i klienta, který u trenéra ještě ani jednou nebyl
  // (typicky nový převedený klient před prvním tréninkem). Analýza ho potom sama převezme.
  // V náhledu (jenCteni) se klienti nenačítají, potřebuje je jen úprava pevných klientů.
  const { data: clientsData } = useApiData(jenCteni ? null : "/api/clients");
  const activeClients = useMemo(() => {
    if (!clientsData?.clients) return [];
    const pilotLower = (activeTrener || "").toLowerCase().trim();
    return clientsData.clients
      .filter((c) => c.stav === "Aktivní" && (c.klient || "").trim())
      .map((c) => ({
        name: c.klient,
        trener: c.trener || "",
        // isCizi = klient má v Stav 2026 jiného oficiálního trenéra
        isCizi: pilotLower && (c.trener || "").toLowerCase().trim() !== pilotLower,
      }))
      .sort((a, b) => a.name.localeCompare(b.name, "cs"));
  }, [clientsData, activeTrener]);

  const handleRefresh = useCallback(async () => {
    if (!activeTrener) return;
    await refetch();
  }, [activeTrener, refetch]);

  const handleCellEdit = useCallback(async (cell, newType, klient, trialDate) => {
    if (!editable || !activeTrener) return;
    // Snapshot předchozí stav pro undo
    pushHistory(`Slot ${["", "Po", "Út", "St", "Čt", "Pá"][cell.dow]} ${String(cell.hour).padStart(2,"0")}:00`, [{
      dow: cell.dow, hour: cell.hour, type: cell.type, klienti: cell.klienti || [], overridden: cell.overridden,
    }]);
    // Optimistic UI — okamžitě aktualizovat grid, backend POST na pozadí
    optimisticGridUpdate([{ dow: cell.dow, hour: cell.hour, type: newType, klient, trialDate }]);
    const label = newType === "preliminary"
      ? `Předrezervace: ${klient}`
      : `Slot uložen: ${["", "Po", "Út", "St", "Čt", "Pá"][cell.dow]} ${String(cell.hour).padStart(2,"0")}:00`;
    showToast("ok", label);
    try {
      const payload = { pilot: activeTrener, dow: cell.dow, hour: cell.hour, type: newType };
      if (klient) payload.klient = klient;
      if (trialDate) payload.trialDate = trialDate;
      // 24.8.2026: co ve slotu bylo předtím. Když tam byl pevný klient a slot
      // se přepíná na Nepracuje/Volno/Oběd, backend z toho založí trenérovi
      // úkol na přesun termínu s deadline 7 dní.
      payload.puvodniTyp = cell.type || "";
      payload.puvodniKlienti = cell.klienti || [];
      const r = await apiFetch("/api/trener-rozvrh-override", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!r.ok) {
        // Backend selhal → refetch sync z reality
        await handleRefresh();
        showToast("err", "Uložení selhalo, načítám původní");
      } else {
        const j = await r.json().catch(() => ({}));
        if (j?.presunUkol?.zalozeno > 0) {
          showToast("ok", j.presunUkol.zalozeno === 1
            ? "Trenérovi vznikl úkol přesunout klientovi termín, deadline 7 dní."
            : `Trenérovi vznikly ${j.presunUkol.zalozeno} úkoly na přesun termínu, deadline 7 dní.`);
        }
      }
    } catch (e) {
      await handleRefresh();
      showToast("err", `Chyba: ${e.message}`);
    }
  }, [editable, activeTrener, handleRefresh, showToast, optimisticGridUpdate, pushHistory]);

  const handleCellReset = useCallback(async (cell) => {
    if (!editable || !activeTrener) return;
    // Snapshot pro undo (kdyby user klikl Zpět)
    pushHistory(`Smazáno: ${["", "Po", "Út", "St", "Čt", "Pá"][cell.dow]} ${String(cell.hour).padStart(2,"0")}:00`, [{
      dow: cell.dow, hour: cell.hour, type: cell.type, klienti: cell.klienti || [], overridden: cell.overridden,
    }]);
    // Optimistic: lokálně přepsat na "volno" hned (modal se už zavřel). DELETE na pozadí.
    // Po DELETE refetch v pozadí pro správný auto-detekovaný typ (pokud analýza měla pevného klienta).
    optimisticGridUpdate([{ dow: cell.dow, hour: cell.hour, reset: true }]);
    showToast("ok", "Smazáno");
    try {
      const r = await apiFetch("/api/trener-rozvrh-override", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pilot: activeTrener, dow: cell.dow, hour: cell.hour }),
      });
      if (!r.ok) {
        // Backend selhal → refetch sync z reality (rollback)
        await handleRefresh();
        showToast("err", "Smazání selhalo, načítám původní");
      } else {
        // Refetch v pozadí pro správný auto-detekovaný typ (analýza může mít pevného klienta)
        handleRefresh();
      }
    } catch (e) {
      await handleRefresh();
      showToast("err", `Chyba: ${e.message}`);
    }
  }, [editable, activeTrener, handleRefresh, showToast, optimisticGridUpdate, pushHistory]);

  const handleBulkEdit = useCallback(async (cells, newType) => {
    if (!editable || !activeTrener || !cells?.length) return;
    // Snapshot aktuálních stavů buněk pro undo (najít je v data.grid)
    const snapshots = cells.map((c) => {
      const orig = data?.grid?.find((g) => g.dow === c.dow && g.hour === c.hour);
      return {
        dow: c.dow, hour: c.hour,
        type: orig?.type || "volno",
        klienti: orig?.klienti || [],
        overridden: !!orig?.overridden,
      };
    });
    pushHistory(`${cells.length} slotů → ${newType}`, snapshots);
    // Optimistic UI — okamžitě aktualizovat všechny buňky lokálně
    optimisticGridUpdate(cells.map((c) => ({ dow: c.dow, hour: c.hour, type: newType })));
    showToast("ok", `Uloženo ${cells.length} slotů`);
    // Paralelní POSTy na pozadí — UI už ukazuje výsledek
    Promise.all(cells.map((c) => {
      // 24.8.2026: i u hromadné úpravy posíláme, co ve slotu bylo. Když tam byl
      // pevný klient, backend z toho udělá trenérovi úkol na přesun termínu.
      const orig = data?.grid?.find((g) => g.dow === c.dow && g.hour === c.hour);
      return apiFetch("/api/trener-rozvrh-override", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          pilot: activeTrener, dow: c.dow, hour: c.hour, type: newType,
          puvodniTyp: orig?.type || "",
          puvodniKlienti: orig?.klienti || [],
        }),
      }).then(async (r) => {
        if (!r.ok) return { ok: false, ukolu: 0 };
        const j = await r.json().catch(() => ({}));
        return { ok: true, ukolu: j?.presunUkol?.zalozeno || 0 };
      }).catch(() => ({ ok: false, ukolu: 0 }));
    })).then((results) => {
      const fail = results.filter((x) => !x.ok).length;
      const ukolu = results.reduce((sum, x) => sum + (x.ukolu || 0), 0);
      if (fail > 0) {
        // Některý POST selhal → sync z reality
        handleRefresh();
        showToast("err", `${fail} slotů selhalo, načítám původní`);
      } else if (ukolu > 0) {
        showToast("ok", `Trenérovi vznikly úkoly na přesun termínu (${ukolu}), deadline 7 dní.`);
      }
    });
  }, [editable, activeTrener, handleRefresh, showToast, optimisticGridUpdate, data, pushHistory]);

  // === UNDO — vrátit poslední akci (max 3 zpět) ===
  const handleUndo = useCallback(async () => {
    if (history.length === 0 || !editable || !activeTrener) return;
    const [last, ...rest] = history;
    setHistory(rest);
    showToast("ok", `Vráceno: ${last.label}`);
    // Pro každou snapshot: pokud byla overridden → POST s původním typem; jinak DELETE
    const updates = last.snapshots.map((s) => ({
      dow: s.dow, hour: s.hour, type: s.type, klient: s.klienti?.[0],
    }));
    optimisticGridUpdate(updates.map((u) => ({ ...u, type: u.type })));
    // Backend operations paralelně
    Promise.all(last.snapshots.map((s) => {
      if (s.overridden) {
        // Měl override → vrátit ten override
        return apiFetch("/api/trener-rozvrh-override", {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            pilot: activeTrener,
            dow: s.dow, hour: s.hour,
            type: s.type,
            klient: s.type === "fixed" ? (s.klienti?.[0] || "") : "",
          }),
        });
      } else {
        // Nebyl override (auto) → smazat aktuální override
        return apiFetch("/api/trener-rozvrh-override", {
          method: "DELETE",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ pilot: activeTrener, dow: s.dow, hour: s.hour }),
        });
      }
    })).then(() => {
      // Refetch — bezpečné pro správnou rekonstrukci grid (zejména pro DELETE → auto typ)
      handleRefresh();
    }).catch(() => {});
  }, [history, editable, activeTrener, showToast, optimisticGridUpdate, handleRefresh]);

  const handleAbsenceAdd = useCallback(async (payload) => {
    if (!canEditAbsence || !activeTrener) return;
    // Optimistic UI — okamžitě přidat do data.absences, backend na pozadí
    const tempId = `temp-${Date.now()}`;
    const optimisticAbsence = {
      rowNum: tempId,
      trener: activeTrener,
      dateFrom: payload.dateFrom,
      dateTo: payload.dateTo,
      duvod: payload.duvod || "dovolena",
      poznamka: payload.poznamka || "",
      vytvoreno: new Date().toISOString(),
    };
    if (data?.absences) {
      const newAbsences = [...data.absences, optimisticAbsence].sort((a, b) =>
        a.dateFrom.localeCompare(b.dateFrom)
      );
      setData({ ...data, absences: newAbsences });
    }
    showToast("ok", "Absence uložena");
    // POST na pozadí
    try {
      const r = await apiFetch("/api/trener-rozvrh-absence", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ pilot: activeTrener, userRole: user?.role, ...payload }),
      });
      if (!r.ok) {
        const j = await r.json().catch(() => ({}));
        throw new Error(j.error || "Chyba");
      }
      // Backend OK — refetch v pozadí pro správné rowNum + případnou aktualizaci patternu
      // (absence v okně 6 týdnů ovlivní pattern výpočet)
      handleRefresh();
    } catch (e) {
      // Selhal POST → rollback (refetch načte server state bez naší optimistic absence)
      handleRefresh();
      showToast("err", `Chyba: ${e.message}`);
    }
  }, [canEditAbsence, activeTrener, data, setData, handleRefresh, showToast, user]);

  const handleAbsenceDelete = useCallback(async (rowNum) => {
    if (!canEditAbsence) return;
    // Optimistic UI — okamžitě odstranit z data.absences
    if (data?.absences) {
      setData({ ...data, absences: data.absences.filter((a) => a.rowNum !== rowNum) });
    }
    showToast("ok", "Absence smazána");
    try {
      const r = await apiFetch("/api/trener-rozvrh-absence", {
        method: "DELETE",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ rowNum }),
      });
      if (!r.ok) throw new Error("Chyba");
      // Refetch v pozadí pro pattern (absence v okně 6 týdnů ovlivňuje výpočet)
      handleRefresh();
    } catch (e) {
      handleRefresh();
      showToast("err", `Chyba: ${e.message}`);
    }
  }, [canEditAbsence, data, setData, handleRefresh, showToast]);

  if (!activeTrener) return <div className="text-sm text-slate-500 italic p-4">Nejdřív si vyber trenéra.</div>;
  if (!data) {
    return (
      <div className="space-y-4">
        <div>
          <h2 className="text-xl font-black text-slate-800">Rozvrh: {activeTrener}</h2>
          <p className="text-sm text-slate-500">Načítám aktuální rozvrh.</p>
        </div>
        <div className="bg-white rounded-xl border border-slate-200 p-4">
          <div className="grid grid-cols-5 gap-2">
            {Array.from({ length: 30 }, (_, i) => (
              <div key={i} className="h-[60px] rounded-xl bg-slate-100 animate-pulse" />
            ))}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 relative">
      {/* Hlavička stránky, jednotná s celým portálem (fáze 3, 3.9.2026).
          6. 10. 2026: manažerská stránka má hlavičku nad kartami trenérů, tady se pak nekreslí. */}
      {!bezHlavicky && (
      <div className="rounded-2xl bg-gradient-to-r from-brand-950 via-brand-900 to-brand-700 p-4 text-white shadow-glass sm:p-5">
        <div className="flex min-w-0 items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl border border-white/15 bg-white/10 sm:h-11 sm:w-11">
            <CalendarDays className="h-5 w-5" />
          </span>
          <div className="min-w-0 flex-1">
            <h2 className="font-serif text-xl font-bold leading-tight sm:text-2xl">Vzorový rozvrh</h2>
          </div>
        </div>
      </div>
      )}

      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-4 z-50 px-4 py-3 rounded-xl shadow-2xl text-sm font-semibold border ${
          toast.type === "ok"
            ? "bg-emerald-50 text-emerald-800 border-emerald-300"
            : "bg-rose-50 text-rose-800 border-rose-300"
        }`}>
          {toast.msg}
        </div>
      )}

      {/* Busy overlay */}
      {busy && (
        <div className="fixed top-4 left-1/2 -translate-x-1/2 z-50 px-4 py-2 rounded-xl bg-brand-600 text-white text-xs font-semibold shadow-2xl">
          Ukládám…
        </div>
      )}

      <RozvrhVzorGrid
        data={data}
        editable={editable}
        jenCteni={jenCteni}
        canFixClient={canFixClient}
        canEditAbsence={canEditAbsence}
        canBackdateAbsence={canBackdateAbsence}
        minAbsenceDaysAhead={minAbsenceDaysAhead}
        activeClients={activeClients}
        onCellEdit={handleCellEdit}
        onCellReset={handleCellReset}
        onBulkEdit={handleBulkEdit}
        onAbsenceAdd={handleAbsenceAdd}
        onAbsenceDelete={handleAbsenceDelete}
        onRefresh={handleRefresh}
        refreshing={refreshing || busy}
        onUndo={handleUndo}
        undoCount={history.length}
        undoLabel={history[0]?.label}
      />
    </div>
  );
}
